import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  FiChevronLeft,
  FiChevronRight,
  FiBriefcase,
  FiSearch,
  FiUserPlus,
  FiX,
} from "react-icons/fi";
import NavTop from "../NavTop/NavTop";
import Loader from "../Loader";
import IsMobile from "../../Others/IsMobile/IsMobile";
import NavItemsContainer from "./Components/NavItemsContainer";
import {
  activeItemForPath,
  sectionIdForPath,
  sectionsForRole,
} from "./DashboardNavigation";
import {
  listMerchants,
  removeMerchantAction,
  selectMerchant,
} from "../../../redux/merchant/merchantActions";
import "react-perfect-scrollbar/dist/css/styles.css";
import "./DashboardShell.css";
import "./DashboardFoundation.css";

const roleName = (role) => {
  if (role === "ROLE_ADMIN") return "Administrator";
  if (role === "ROLE_SUPER_ADMIN") return "Super admin";
  return "Merchant";
};

const Drawer = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = IsMobile();
  const workspaceMainRef = useRef(null);

  const userState = useSelector((state) => state.userState);
  const { userInfo, userData } = userState;
  const merchantState = useSelector((state) => state.merchantState);
  const { merchants = [], loading, selectedMerchant } = merchantState;

  const role = userInfo?.role || "ROLE_ADMIN";
  const sections = useMemo(() => sectionsForRole(role), [role]);
  const inferredSection = sectionIdForPath(location.pathname, role);
  const [activeSectionId, setActiveSectionId] = useState(inferredSection);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => location.pathname.startsWith("/app-page/edit"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [merchantSearch, setMerchantSearch] = useState("");

  useEffect(() => {
    setActiveSectionId(sectionIdForPath(location.pathname, role));
    setMobileOpen(false);
    if (workspaceMainRef.current) workspaceMainRef.current.scrollTop = 0;
  }, [location.pathname, role]);

  useEffect(() => {
    if (activeSectionId === "merchants" && role === "ROLE_ADMIN" && merchants.length === 0) {
      dispatch(listMerchants());
    }
  }, [activeSectionId, dispatch, merchants.length, role]);

  const activeSection = sections.find((section) => section.id === activeSectionId) || sections[0];
  const activeItem = activeItemForPath(sections, location.pathname);
  const merchantResults = useMemo(() => {
    const query = merchantSearch.trim().toLowerCase();
    if (!query) return merchants;
    return merchants.filter((merchant) => merchant.name?.toLowerCase().includes(query));
  }, [merchantSearch, merchants]);

  const openSection = (section) => {
    setActiveSectionId(section.id);
    if (sidebarCollapsed) setSidebarCollapsed(false);
  };

  const dispatchMerchant = (merchant) => {
    dispatch(selectMerchant(merchant.name, merchant._id));
    navigate(`/merchant/view/${merchant._id}`);
    setMobileOpen(false);
  };

  const removeMerchant = () => dispatch(removeMerchantAction());

  const merchantItems = selectedMerchant
    ? [
        ...(userData?.permissions?.includes("W_MERCHANTS")
          ? [
              { link: `/merchant/view/${selectedMerchant.id}`, text: "Overview", icon: FiBriefcase },
              { link: `/merchant/products?mid=${selectedMerchant.id}&status=all`, text: "Products", icon: FiBriefcase },
              { link: `/merchant/listbrands/${selectedMerchant.id}`, text: "Brands", icon: FiBriefcase },
            ]
          : []),
        ...(userData?.permissions?.includes("R_ORDERS")
          ? [{ link: `/admin/orders/${selectedMerchant.name}/${selectedMerchant.id}?page=1`, text: "Orders", icon: FiBriefcase }]
          : []),
        { link: "/payouts", text: "Payouts", icon: FiBriefcase },
      ]
    : [];

  const roleItems = (activeSection?.items || []).map((entry) =>
    entry.appendMerchantQuery
      ? { ...entry, link: `${entry.link}?mid=${userData?._id}&status=all` }
      : entry
  );

  const AdminMerchants = () => (
    <div className="dashboard-merchant-picker">
      <div className="dashboard-merchant-picker__head">
        <div>
          <strong>Choose a merchant</strong>
          <small>Open their products, orders and payouts.</small>
        </div>
        {userData?.permissions?.includes("W_MERCHANTS") && (
          <Link to="/createmerchant" title="Add merchant" onClick={() => setMobileOpen(false)}>
            <FiUserPlus />
          </Link>
        )}
      </div>
      <label className="dashboard-merchant-search">
        <FiSearch />
        <input value={merchantSearch} onChange={(event) => setMerchantSearch(event.target.value)} placeholder="Search merchants" />
      </label>
      <div className="dashboard-merchant-results">
        {loading ? (
          <Loader />
        ) : merchantResults.length ? (
          merchantResults.map((merchant) => (
            <button type="button" key={merchant._id} onClick={() => dispatchMerchant(merchant)}>
              <span>{merchant.name?.slice(0, 1)?.toUpperCase()}</span>
              <span><strong>{merchant.name}</strong><small>{merchant.brands?.length || 0} brands · {merchant.currencyType || "No currency"}</small></span>
              <FiChevronRight />
            </button>
          ))
        ) : (
          <p>No merchants match “{merchantSearch}”.</p>
        )}
      </div>
    </div>
  );

  const skipShell = /login|unauthorized|reset|pageview|postedit/.test(location.pathname);
  if (skipShell) return <div>{children}</div>;

  const isWorkspaceRoute =
    location.pathname.startsWith("/app-page/edit/") ||
    location.pathname === "/pogi-conversation" ||
    location.pathname === "/chat";

  return (
    <div className={`dashboard-shell ${sidebarCollapsed ? "is-collapsed" : ""} ${mobileOpen ? "is-mobile-open" : ""}`}>
      <button type="button" className="dashboard-shell__backdrop" onClick={() => setMobileOpen(false)} aria-label="Close navigation" />

      <aside className="dashboard-sidebar" aria-label="Dashboard navigation">
        <div className="dashboard-sidebar__brand">
          <Link to={role === "ROLE_ADMIN" ? "/admin" : role === "ROLE_SUPER_ADMIN" ? "/dashboard" : "/"}>
            <span>A</span>
            <span className="dashboard-sidebar__brand-copy"><strong>Arktastic</strong><small>Admin</small></span>
          </Link>
          <button type="button" className="dashboard-sidebar__mobile-close" onClick={() => setMobileOpen(false)} aria-label="Close navigation"><FiX /></button>
        </div>

        <div className="dashboard-sidebar__scroll">
          {!sidebarCollapsed && <p className="dashboard-sidebar__label">Workspaces</p>}
          <nav className="dashboard-sidebar__sections">
            {sections.map((section) => {
              const Icon = section.icon;
              const isActive = section.id === activeSectionId;
              return (
                <div key={section.id} className={`dashboard-nav-section ${isActive ? "is-active" : ""} ${section.appOnly ? "is-app" : ""}`}>
                  <button type="button" onClick={() => openSection(section)} title={sidebarCollapsed ? section.label : undefined}>
                    <span className="dashboard-nav-section__icon"><Icon /></span>
                    <span className="dashboard-nav-section__copy"><strong>{section.label}</strong><small>{section.description}</small></span>
                    {section.appOnly && <em>App only</em>}
                    {!sidebarCollapsed && <FiChevronRight className="dashboard-nav-section__chevron" />}
                  </button>

                  {isActive && !sidebarCollapsed && (
                    <div className="dashboard-nav-section__items">
                      {section.merchantWorkspace ? (
                        selectedMerchant ? (
                          <>
                            <div className="dashboard-selected-merchant">
                              <span>{selectedMerchant.name?.slice(0, 1)?.toUpperCase()}</span>
                              <div><strong>{selectedMerchant.name}</strong><small>Selected merchant</small></div>
                              <button type="button" onClick={removeMerchant}>Change</button>
                            </div>
                            <NavItemsContainer list={merchantItems} setIsOpen={setMobileOpen} />
                          </>
                        ) : <AdminMerchants />
                      ) : (
                        <NavItemsContainer list={roleItems} setIsOpen={setMobileOpen} appOnly={section.appOnly} />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>

        <div className="dashboard-sidebar__footer">
          {!sidebarCollapsed && (
            <div className="dashboard-sidebar__user">
              <span>{userInfo?.firstName?.slice(0, 1)?.toUpperCase() || "A"}</span>
              <div><strong>{userInfo?.firstName} {userInfo?.lastName}</strong><small>{roleName(role)}</small></div>
            </div>
          )}
          {!isMobile && (
            <button
              type="button"
              className="dashboard-sidebar__collapse"
              onClick={() => setSidebarCollapsed((current) => !current)}
              aria-label={sidebarCollapsed ? "Expand navigation" : "Collapse navigation"}
              title={sidebarCollapsed ? "Expand navigation" : "Collapse navigation"}
            >
              {sidebarCollapsed ? <FiChevronRight /> : <><FiChevronLeft /><span>Collapse navigation</span></>}
            </button>
          )}
        </div>
      </aside>

      <div className="dashboard-shell__workspace">
        <NavTop
          isOpen={mobileOpen}
          setIsOpen={setMobileOpen}
          sectionLabel={activeSection?.label}
          pageLabel={activeItem?.text || activeSection?.description}
        />
        <div ref={workspaceMainRef} className="dashboard-shell__main">
          <div className={`dashboard-route ${isWorkspaceRoute ? "is-workspace-route" : ""}`}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Drawer;
